import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "h-full min-w-[300px] bg-black !flex-[10]"
}
const _hoisted_2 = {
  key: 1,
  class: "flex flex-col w-full h-full gap-4 py-5"
}
const _hoisted_3 = {
  key: 0,
  class: "absolute top-0 right-0 z-10 flex items-center w-8 h-full"
}
const _hoisted_4 = {
  key: 1,
  class: "absolute top-0 right-0 z-10 flex items-center w-8 h-full"
}

import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, PropType, provide, ref, Ref, watch } from 'vue';
import slugify from 'slugify';

import { useGetDeviceQuery } from '@/entities/device/hooks';
import { useGetInspectionQuery, useGetInspectionsQuery } from '@/entities/inspection/hooks';
import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { useGetServiceQuery } from '@/entities/services/hooks';

import { Annotation } from '@/entities/annotation/types';
import { Inspection } from '@/entities/inspection/types';

import { NkButton, NkGroup, NkStack, NkInlineModal } from '@/core/components';
import VoldateMeasureCreateForm
  from '@/entities/volta-measure/components/form/VoltaMeasureCreateForm.vue';
import AnnotationTable from '@/entities/annotation/components/AnnotationTable.vue';
import VoltaMeasuresTable from '@/entities/volta-measure/components/VoltaMeasureTable.vue';
import InspectionViewer from '@/teams/visionair/components/InspectionViewer/InspectionViewer.vue';
import ImageAssembler from '@/teams/visionair/components/ImageAssembler/ImageAssembler.vue';
import ImageAutoAssembler from '@/teams/visionair/components/ImageAssembler/ImageAutoAssembler.vue';
import { useGetDeviceGroupQuery } from '@/entities/device-group/hooks';
import { OrganizationInjectionKey } from '@/teams/visionair/components/keys';
import { serviceInjectionKey } from '@/entities/services/keys';

export type TargetAnnotation = {
  id: Annotation['id'],
  type: ['click', 'hover'][number],
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AnnotationWorkspace',
  props: {
  inspectionId: {
    type: Number as PropType<Inspection['id']>,
    required: true,
  },
},
  setup(__props) {

const props = __props

const route = useRoute();
const router = useRouter();



const controls = [
  {
    name: 'Global',
    value: 6,
    icon: 'NkReceipt',
    access: 'all',
  },
  {
    name: 'Inline',
    value: 2,
    icon: 'NkViewSidebar',
    access: 'all',
  },
  {
    name: 'Default',
    value: 1,
    icon: 'NkViewWeek',
    access: 'all',
  },
  {
    name: 'Full',
    value: 3,
    icon: 'NkAddPhoto',
    access: 'all',
  },
  {
    name: 'Assembly',
    value: 4,
    icon: 'NkDashboard',
    access: 'all',
  },
  {
    name: 'AutoAssembly',
    value: 5,
    icon: 'NkViewAgenda',
    access: 'admin',
  },
];

const controlsVolta = [
  {
    name: 'Global',
    value: 6,
    icon: 'NkReceipt',
    access: 'all',
  },
  {
    name: 'Inline',
    value: 2,
    icon: 'NkViewSidebar',
    access: 'all',
  }
];



const activeControlValue = ref(2);
const targetAnnotation: Ref<{ id: Annotation['id'], type: string } | null> = ref(null);

const isFullscreen = ref(false);

// Fetching inspection
const inspectionQueryData = computed(() => ({ id: props.inspectionId }));
const hasInspectionQueryData = computed(() => !!inspectionQueryData.value.id);
const { data: inspection } = useGetInspectionQuery(inspectionQueryData, {
  enabled: hasInspectionQueryData,
});

// Fetching device
const deviceQueryData: Ref = computed(() => ({ id: inspection.value?.device }));
const hasDeviceQueryData = computed(() => !!deviceQueryData.value.id);
const { data: device } = useGetDeviceQuery(deviceQueryData, {
  enabled: hasDeviceQueryData,
});

// Fetching inspectionGroup
const inspectionGroupQueryData: Ref = computed(() => ({
  id: inspection.value?.inspectionGroup,
}));

const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const test = computed(() => ({ inspectionGroup: inspection.value?.inspectionGroup }))
const { data: allInspections } = useGetInspectionsQuery({ enabled:  hasInspectionGroupQueryData}, test)

// console.log(allInspections)

const allAnnotations = computed(() => {
  const _ = allInspections.value?.filter(e => e.annotations.length !== 0)
  if (_?.length === 0) {
     return []
  }
  return _.map(e => e.annotations).reduce((e1, e2) => e1.concat(e2))
})

const allAnnotationsVolta = computed(() => {
  const _ = allInspections.value?.filter(e => e.voltaMeasures.length !== 0)
  if (_?.length === 0) {
    return []
  }
  return _.map(e => e.voltaMeasures).reduce((e1, e2) => e1.concat(e2))
})

const { data: inspectionGroup } = useGetInspectionGroupQuery(inspectionGroupQueryData, {
  enabled: hasInspectionGroupQueryData,
});

// Fetching inspectionGroup Service
const serviceQueryData: Ref = computed(() => ({
  id: inspectionGroup.value?.service,
}));
const hasServiceQueryData = computed(() => !!serviceQueryData.value.id);
const { data: service } = useGetServiceQuery(serviceQueryData, {
  enabled: hasServiceQueryData,
});

const isCertifiedVolta = computed(() => service.value?.id === 9 || service.value?.id === 10 || service.value?.id === 12);
const isVoltaExpress = computed(() => service.value?.id === 8);

provide(serviceInjectionKey, {
  service,
  isCertifiedVolta,
  isVoltaExpress
});

const rootDeviceQueryData: Ref = computed(() => ({
  id: inspectionGroup.value?.device,
}));
const hasRootDeviceQueryData = computed(() => !!rootDeviceQueryData.value.id);
const { data: rootDevice } = useGetDeviceQuery(rootDeviceQueryData, {
  enabled: hasRootDeviceQueryData,
});

const deviceGroupQueryData: Ref = computed(() => ({
  id: rootDevice.value?.deviceGroup
}));
const hasDeviceGroupQueryData = computed(() => !!deviceGroupQueryData.value.id);
const { data: deviceGroup } = useGetDeviceGroupQuery(deviceGroupQueryData, {
  enabled: hasDeviceGroupQueryData
})

provide(OrganizationInjectionKey,{
   organizationId: computed(() => deviceGroup.value?.organization),
})

const isVoltaInspection = computed(() => slugify(service.value?.name || '', { lower: true }).includes('volta'));
const isSherlockMotionInspection = computed(() => slugify(service.value?.name || '', { lower: true}).includes('motion'));


// Fetching for annotations
const annotationIds = computed(() => inspection.value?.annotations || []);

// Fetching for VoltaMeasures
const voltaMeasuresIds = computed(() => inspection.value?.voltaMeasures || []);

// const getTableColumnFilters = computed(() => activeControlValue.value === 1 || activeControlValue.value === 6 ? [] : ['problem-type', 'severity']);

const getTableColumnFilters = computed(() => {
  const fields = ['problem-type', 'hub-distance', 'severity', 'history', 'comment', 'user', 'defect-size'];

  switch (activeControlValue.value) {
    case 1:
      return isSherlockMotionInspection.value ? [...fields, 'defect-size', 'annotation-id'] : [...fields];
    case 6:
      return isSherlockMotionInspection.value ? [] : [...fields, 'inspection'];
    default:
      return ['problem-type', 'severity'];
  }
})

const setTargetAnnotation = (id: TargetAnnotation['id'], type: TargetAnnotation['type']) => {
  targetAnnotation.value = { id, type };
};

const onCancel = () => {
  targetAnnotation.value = null;
};

const switchView = async (value: number) => {
  await router.push({
    ...route,
    query: {
      ...(route.query || {}),
      view: value,
    },
  });
  activeControlValue.value = value
}

// watch(activeControlValue, () => {
//   router.push({
//     ...route,
//     query: {
//       ...(route.query || {}),
//       view: activeControlValue.value,
//     },
//   });
// });

watch(() => route.query.view, () => {
  activeControlValue.value = Number(route.query.view);
})

onMounted(() => {
  if (route.query?.view) {
    activeControlValue.value = Number(route.query.view);
  }
});

provide('injectedWorkspace', {isFullscreen})

return (_ctx: any,_cache: any) => {
  const _component_fullscreen = _resolveComponent("fullscreen")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([{
    '!pl-4': activeControlValue.value !== 1,
      }, "relative h-full pl-8 pr-8"])
  }, [
    _createVNode(_component_fullscreen, {
      class: "h-full",
      modelValue: isFullscreen.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isFullscreen).value = $event))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(NkStack), {
          class: _normalizeClass([{
            'py-5': activeControlValue.value === 1,
          }, "h-full"]),
          spacing: 6
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(NkGroup), {
              class: "flex-1 min-h-0 bg-white",
              spacing: 4,
              "no-wrap": "",
              align: "flex-start"
            }, {
              default: _withCtx(() => [
                (_unref(service) && !_unref(isVoltaInspection))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      (activeControlValue.value === 2 || activeControlValue.value === 3)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                            (_unref(inspection)?.device)
                              ? (_openBlock(), _createBlock(InspectionViewer, {
                                  key: 0,
                                  "target-annotation": targetAnnotation.value,
                                  inspection: props.inspectionId,
                                  class: "h-full",
                                  onCancel: onCancel
                                }, null, 8, ["target-annotation", "inspection"]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true),
                      ((activeControlValue.value === 1 || activeControlValue.value === 2 || activeControlValue.value === 6) && !_unref(isVoltaInspection))
                        ? (_openBlock(), _createBlock(AnnotationTable, {
                            key: activeControlValue.value,
                            "annotation-ids": activeControlValue.value === 6 ? _unref(allAnnotations) : _unref(annotationIds),
                            pick: _unref(getTableColumnFilters),
                            rootDevice: _unref(rootDevice),
                            class: _normalizeClass({
                    'py-5': activeControlValue.value === 2 || activeControlValue.value === 6
                  }),
                            onAnnotationRowHover: _cache[0] || (_cache[0] = (id) => setTargetAnnotation(id, 'hover')),
                            onAnnotationRowClick: _cache[1] || (_cache[1] = (id) => setTargetAnnotation(id, 'click'))
                          }, null, 8, ["annotation-ids", "pick", "rootDevice", "class"]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : (_unref(service) && _unref(isVoltaInspection))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        (_ctx.$store.getters['connection/isGranted'](['volta_measure_create']))
                          ? (_openBlock(), _createBlock(_unref(NkGroup), {
                              key: 0,
                              position: "right"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(NkInlineModal), {
                                  title: _ctx.$t('volta-measure.action.add'),
                                  class: "max-w-screen-md"
                                }, {
                                  content: _withCtx(({close}) => [
                                    _createVNode(VoldateMeasureCreateForm, { onSubmitted: close }, null, 8, ["onSubmitted"])
                                  ]),
                                  default: _withCtx(() => [
                                    _createVNode(_unref(NkButton), { icon: "NkPlus" }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_ctx.$t('volta-measure.action.add')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }, 8, ["title"])
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true),
                        _createVNode(VoltaMeasuresTable, {
                          "measures-ids": activeControlValue.value === 6 ? _unref(allAnnotationsVolta) : _unref(voltaMeasuresIds)
                        }, null, 8, ["measures-ids"])
                      ]))
                    : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            ([4].includes(activeControlValue.value) && _unref(inspection)?.inspectionGroup)
              ? (_openBlock(), _createBlock(ImageAssembler, {
                  key: 0,
                  inspection: Number(_ctx.$route.params.inspection),
                  "inspection-group": _unref(inspection)?.inspectionGroup,
                  class: "w-full h-full"
                }, null, 8, ["inspection", "inspection-group"]))
              : _createCommentVNode("", true),
            (_ctx.$store.getters['connection/isGranted'] && [5].includes(activeControlValue.value) && _unref(inspection)?.inspectionGroup)
              ? (_openBlock(), _createBlock(ImageAutoAssembler, {
                  key: 1,
                  inspection: Number(_ctx.$route.params.inspection),
                  "inspection-group": _unref(inspection)?.inspectionGroup,
                  class: "w-full h-full"
                }, null, 8, ["inspection", "inspection-group"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["class"]),
        (!_unref(isVoltaInspection))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_unref(NkStack), {
                justify: "center",
                align: "center",
                class: "w-full py-5",
                spacing: 4
              }, {
                default: _withCtx(() => [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(controls, (control) => {
                    return _createElementVNode("div", {
                      key: control.value
                    }, [
                      (control.access === 'all' || control.access ==='admin' && _ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']))
                        ? (_openBlock(), _createBlock(_unref(NkButton), {
                            key: 0,
                            onClick: ($event: any) => (switchView(control.value)),
                            icon: control.icon,
                            plain: activeControlValue.value === control.value,
                            circle: "",
                            size: "sm"
                          }, null, 8, ["onClick", "icon", "plain"]))
                        : _createCommentVNode("", true)
                    ])
                  }), 64))
                ]),
                _: 1
              })
            ]))
          : (_unref(isVoltaInspection))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createVNode(_unref(NkStack), {
                  justify: "center",
                  align: "center",
                  class: "w-full py-5",
                  spacing: 4
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(controlsVolta, (control) => {
                      return _createElementVNode("div", {
                        key: control.value
                      }, [
                        (control.access === 'all' || control.access ==='admin' && _ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']))
                          ? (_openBlock(), _createBlock(_unref(NkButton), {
                              key: 0,
                              onClick: ($event: any) => (switchView(control.value)),
                              icon: control.icon,
                              plain: activeControlValue.value === control.value,
                              circle: "",
                              size: "sm"
                            }, null, 8, ["onClick", "icon", "plain"]))
                          : _createCommentVNode("", true)
                      ])
                    }), 64))
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 2))
}
}

})